import { useIsMounted } from '@simplywallst/ui-core';
import type { ChangeEvent, ReactNode } from 'react';
import { useId, useRef } from 'react';
import { SliderLabels } from './components/SliderLabels';
import { SliderTextInput } from './components/SliderTextInput';
import type { SliderTrackBandInput } from './utils';
import {
  sliderNormaliseBands,
  sliderTrackBandColor,
  sliderValuePercentage,
} from './utils';
import { parseNumberSafe } from '../../../Narrative/utils';
import { InputRange } from './components/InputRange';

type Props = {
  label?: string;
  value: number;
  precision: number;
  minRange: number;
  maxRange: number;
  maxManual?: number;
  bands?: SliderTrackBandInput[];
  suffix?: ReactNode;
  onChange: (value: number) => void;
};

export const Slider = ({
  value,
  label,
  precision,
  onChange,
  minRange,
  maxRange,
  maxManual,
  suffix,
  bands,
}: Props) => {
  const currentMarkerRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isMounted = useIsMounted();

  const handleSliderChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(parseNumberSafe(e.target.value, precision));
  };

  const normalisedBands = sliderNormaliseBands(
    bands,
    minRange,
    maxRange,
    'var(--s-color-p-white-170)'
  );
  const sliderPercentage = sliderValuePercentage(value, minRange, maxRange);
  const inputId = useId();
  return (
    <div className="pt-x4" ref={containerRef}>
      {label && (
        <label htmlFor={inputId} className="sr-only">
          {label}
        </label>
      )}
      <SliderTextInput
        id={inputId}
        value={value}
        precision={precision}
        suffix={suffix}
        onChange={onChange}
        isValueValid={(newValue) =>
          newValue <= (maxManual ?? maxRange) && newValue >= minRange
        }
        anchorEl={isMounted ? currentMarkerRef.current : null}
        boundaryEl={isMounted ? containerRef.current : null}
      />

      <div className="relative h-x3">
        <div
          ref={currentMarkerRef}
          className="absolute top-0 size-px"
          style={{
            left: `${sliderPercentage}%`,
          }}
        />
        <InputRange
          className="absolute inset-0 z-10"
          min={minRange}
          max={maxRange}
          step="1"
          value={value}
          onChange={handleSliderChange}
          trackColor={sliderTrackBandColor(normalisedBands)}
        />
      </div>

      <SliderLabels bands={normalisedBands} />
    </div>
  );
};
